import * as React from "react";
import { select, useSelect } from "@hubper/theme";
import { animated, useSpring } from "react-spring";
import { useGesture } from "react-use-gesture";
import composeRefs from "../../util/composeRefs";

import styled from "styled-components";

const BAR_HEIGHT = 5;
const CIRCLE_SIZE = 18;

const Base = styled.div`
  position: relative;
  height: ${CIRCLE_SIZE}px;
`;

const Bar = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  top: 50%;
  border-radius: 5px;
  transform: translateY(-50%);
  height: ${BAR_HEIGHT}px;
  overflow: hidden;
  background-color: ${select.color(["grey", 1])};
`;

const InnerBar = styled(animated.div)`
  position: absolute;
  top: 0;
  height: 100%;
  /* background-color: ${select.color(["primary", 1])}; */
  cursor: pointer;
  cursor: -webkit-grab;

  &:active {
    cursor: -webkit-grabbing;
  }
`;

const Circle = styled(animated.div)`
  position: absolute;
  top: 0;
  left: 0;
  border-radius: 50%;
  width: ${CIRCLE_SIZE}px;
  height: ${CIRCLE_SIZE}px;
  background-color: ${select.color(["primary", 5])};
  cursor: pointer;
  cursor: -webkit-grab;

  &:active {
    cursor: -webkit-grabbing;
  }
`;

interface GeneralProps {
  style?: React.CSSProperties;
  className?: string;
  min?: number;
  max?: number;
  step?: number;
}

interface SingleProps extends GeneralProps {
  value: number;
  onChange: (value: number) => void;
}

interface RangeProps extends GeneralProps {
  value: [number, number];
  onChange: (value: [number, number]) => void;
}

function useDimensions() {
  const ref = React.useRef<HTMLDivElement>();
  const [state, setState] = React.useState({ width: 0, height: 0 });

  React.useLayoutEffect(() => {
    const { width, height } = ref.current!.getBoundingClientRect();
    setState({ width, height });
  }, []);

  return [ref, state] as [any, typeof state];
}

function round(num: number, increment: number, offset: number) {
  return Math.ceil((num - offset) / increment) * increment + offset;
}

type UseSliderProps = {
  value: number;
  width: number;
  min: number;
  max: number;
  onChange: (value: number) => void;
  step: number;
};

function useSlider({ value, width, onChange, min, max, step }: UseSliderProps) {
  const lastValue = React.useRef<number>(value);

  const [isDragging, setDragging] = React.useState(false);

  const bind = useGesture({
    onDragEnd: () => {
      setDragging(false);
    },
    onDragStart: (props: any) => {
      lastValue.current = value;
      setDragging(true);
      props.event.preventDefault();
    },
    onDrag: ({ delta }) => {
      const movedValue = (max - min) * (delta[0] / width);

      let newValue = lastValue.current! + movedValue;
      if (newValue < min) {
        newValue = min;
      }
      if (newValue > max) {
        newValue = max;
      }

      onChange(round(newValue, step, min));
    }
  });

  const x = ((value - min) / (max - min)) * width;

  return [bind as any, x as number, isDragging as boolean];
}

type UseBarDragProps = {
  value: [number, number];
  width: number;
  min: number;
  max: number;
  onChange: (value: [number, number]) => void;
  step: number;
};

function useBarDrag({
  value,
  width,
  onChange,
  min,
  max,
  step
}: UseBarDragProps) {
  const lastValue = React.useRef<[number, number]>(value);

  const [isDragging, setDragging] = React.useState(false);

  const bind = useGesture({
    onDragEnd: () => {
      setDragging(false);
    },
    onDragStart: (props: any) => {
      lastValue.current = value;
      setDragging(true);
      props.event.preventDefault();
    },
    onDrag: ({ delta }) => {
      const movedValue = (max - min) * (delta[0] / width);

      const [first, second] = lastValue.current!;

      if (first + movedValue < min) {
        onChange([round(min, step, min), round(second - first, step, min)] as [
          number,
          number
        ]);
        return;
      }

      if (second + movedValue > max) {
        onChange([
          round(max - (second - first), step, min),
          round(max, step, min)
        ] as [number, number]);
        return;
      }

      onChange([
        round(first + movedValue, step, min),
        round(second + movedValue, step, min)
      ] as [number, number]);
    }
  });

  return [bind as any, isDragging as boolean];
}

function useCircleAnimation(isDragging: boolean) {
  const [isOver, setOver] = React.useState(false);
  const { color } = useSelect();

  const style = useSpring({
    backgroundColor: color(["primary", isDragging ? 4 : isOver ? 6 : 5]),
    boxShadow: `0px 1px 2px 0.5px rgba(0, 0, 0, ${isDragging ? 0.4 : 0.2})`
  });

  return [
    style,
    {
      onMouseEnter: () => setOver(true),
      onMouseLeave: () => setOver(false)
    }
  ] as any;
}

export const Single = React.forwardRef(function Single(
  {
    style,
    className,
    value = 0,
    min = 0,
    max = 100,
    onChange,
    step = 1
  }: SingleProps,
  ref: any
) {
  const [baseRef, { width }] = useDimensions();

  const [bind, x, isDragging] = useSlider({
    value,
    width,
    onChange,
    min,
    max,
    step
  });

  const [aniStyle, isOverBind] = useCircleAnimation(isDragging);

  return (
    <Base ref={composeRefs(ref, baseRef)} style={style} className={className}>
      <Bar />
      <Circle
        {...bind()}
        {...isOverBind}
        style={{
          transform: `translateX(${x - CIRCLE_SIZE / 2}px)`,
          ...aniStyle
        }}
      />
    </Base>
  );
});

export const Range = React.forwardRef(function Single(
  {
    style,
    className,
    value = [0, 0],
    min = 0,
    max = 100,
    onChange,
    step = 1
  }: RangeProps,
  ref: any
) {
  const [baseRef, { width }] = useDimensions();

  const { color } = useSelect();

  const [lowerBind, lowerX, isDraggingLower] = useSlider({
    value: value[0],
    width,
    onChange: val => {
      let newValue = val;
      if (val > value[1]) {
        newValue = value[1];
      }

      onChange([newValue, value[1]]);
    },
    min,
    max,
    step
  });

  const [upperBind, upperX, isDraggingUpper] = useSlider({
    value: value[1],
    width,
    onChange: val => {
      let newValue = val;
      if (val < value[0]) {
        newValue = value[0];
      }

      onChange([value[0], newValue]);
    },
    min,
    max,
    step
  });

  const [isBarOver, setBarOver] = React.useState(false);

  const [barBind, isBarDragging] = useBarDrag({
    value,
    width,
    onChange,
    min,
    max,
    step
  });

  const [lowerAniStyle, lowerIsOverBind] = useCircleAnimation(isDraggingLower);
  const [upperAniStyle, upperIsOverBind] = useCircleAnimation(isDraggingUpper);

  const barStyle = useSpring({
    backgroundColor: color(["primary", isBarDragging ? 3 : isBarOver ? 2 : 1])
    // boxShadow: `0px 1px 2px 0.5px rgba(0, 0, 0, ${isBarDragging ? 1 : 0})`
  });

  return (
    <Base ref={composeRefs(ref, baseRef)} style={style} className={className}>
      <Bar>
        <InnerBar
          style={{ left: lowerX, right: width - upperX, ...barStyle }}
          {...barBind()}
          onMouseEnter={() => setBarOver(true)}
          onMouseLeave={() => setBarOver(false)}
        />
      </Bar>
      <Circle
        {...lowerBind()}
        {...lowerIsOverBind}
        style={{
          transform: `translateX(${lowerX - CIRCLE_SIZE / 2}px)`,
          ...lowerAniStyle
        }}
      />
      <Circle
        {...upperBind()}
        {...upperIsOverBind}
        style={{
          transform: `translateX(${upperX - CIRCLE_SIZE / 2}px)`,
          ...upperAniStyle
        }}
      />
    </Base>
  );
});
